import type { KnexPaginatedData } from './knex-paginate';
import type { MentionEntityTagAttributes } from './markdown';
import type {
    QuestDisplayDetails,
    QuestDisplayStatus,
    QuestWaitReason,
} from './questDisplay';
import type { SrtNotificationConfig } from './srtNotification';

export enum QuestStatus {
    ACTIVE = 'active',
    PAUSED = 'paused',
    WAITING_FOR_INPUT = 'waiting_for_input',
    COMPLETED = 'completed',
}

export enum QuestVisibility {
    PRIVATE = 'private',
    PROJECT = 'project',
}

export enum QuestHandoffStatus {
    COMPLETED = 'completed',
    FAILED = 'failed',
}

export enum QuestHandoffPromptStatus {
    PROMPTING = 'prompting',
    PROMPTED = 'prompted',
    FAILED = 'failed',
}

export enum QuestHumanInputStatus {
    UNANSWERED = 'unanswered',
    ANSWERED = 'answered',
    CANCELLED = 'cancelled',
    SUPERSEDED = 'superseded',
}

export enum QuestWaitStatus {
    PENDING = 'pending',
    ACTIVE = 'active',
    RESUMED = 'resumed',
    TIMED_OUT = 'timed_out',
    SUPERSEDED = 'superseded',
}

export enum QuestActivityEventType {
    PROJECT_CREATED = 'PROJECT_CREATED',
    PROJECT_PAUSED = 'PROJECT_PAUSED',
    PROJECT_RESUMED = 'PROJECT_RESUMED',
    PROJECT_STEERED = 'PROJECT_STEERED',
    PROJECT_NUDGED = 'PROJECT_NUDGED',
    OBJECTIVE_UPDATED = 'OBJECTIVE_UPDATED',
    PLAN_UPDATED = 'PLAN_UPDATED',
    TASK_STARTED = 'TASK_STARTED',
    TASK_WAITING = 'TASK_WAITING',
    TASK_HANDOFF_RECEIVED = 'TASK_HANDOFF_RECEIVED',
    TASK_COMPLETED = 'TASK_COMPLETED',
    TASK_FAILED = 'TASK_FAILED',
    HUMAN_INPUT_REQUESTED = 'HUMAN_INPUT_REQUESTED',
    HUMAN_INPUT_ANSWERED = 'HUMAN_INPUT_ANSWERED',
    AUDIENCE_CREATED = 'AUDIENCE_CREATED',
    AUDIENCE_UPDATED = 'AUDIENCE_UPDATED',
    CAMPAIGN_CREATED = 'CAMPAIGN_CREATED',
    CAMPAIGN_UPDATED = 'CAMPAIGN_UPDATED',
    CAMPAIGN_SENT = 'CAMPAIGN_SENT',
    JOURNEY_CREATED = 'JOURNEY_CREATED',
    JOURNEY_UPDATED = 'JOURNEY_UPDATED',
    CUSTOM_ATTRIBUTE_CREATED = 'CUSTOM_ATTRIBUTE_CREATED',
    CUSTOM_ATTRIBUTE_UPDATED = 'CUSTOM_ATTRIBUTE_UPDATED',
}

export enum QuestSteerSource {
    DIRECT = 'direct',
    PERSONAL_CHAT = 'personal_chat',
    NUDGE = 'nudge',
}

export enum QuestPlanItemStatus {
    UPCOMING = 'upcoming',
    IN_PROGRESS = 'in_progress',
    COMPLETED = 'completed',
    CANCELLED = 'cancelled',
}

export enum QuestPlanEntityAction {
    CREATED = 'created',
    UPDATED = 'updated',
}

export enum QuestHealthStatus {
    OK = 'ok',
    POSSIBLY_STUCK = 'possibly_stuck',
}

export enum QuestDriverStatus {
    RUNNING = 'running',
    IDLE = 'idle',
    UNKNOWN = 'unknown',
}

export type QuestPlanEntityReference = MentionEntityTagAttributes & {
    action: QuestPlanEntityAction;
};

export type QuestHandoffEvidence = {
    url: string;
    sourceType: 'first_party' | 'independent' | 'unknown';
    claim: string;
    confidence: 'high' | 'medium' | 'low';
    provisional?: boolean;
    limitation?: string;
};

export type QuestHandoffStructuredData = {
    createdEntities?: MentionEntityTagAttributes[];
    updatedEntities?: MentionEntityTagAttributes[];
    blockingQuestions?: string[];
    recommendedNextActions?: string[];
    evidence?: QuestHandoffEvidence[];
    researchConflicts?: string[];
    researchLimitations?: string[];
};

export type QuestHandoffArtifact = {
    promptStatus?: QuestHandoffPromptStatus;
    promptStatusUpdatedAt?: string;
    structuredData?: QuestHandoffStructuredData | null;
};

export type QuestPlanItem = {
    id: string;
    status: QuestPlanItemStatus;
    title: string;
    summary: string;
    requiresReport?: boolean;
    kickoffApprovalId?: string;
    milestoneReportId?: string;
    detailedPlan?: string;
    outcome?: string;
    startedAt?: string;
    completedAt?: string;
    reasoning: string;
    humanInputIds?: string[];
    entities?: QuestPlanEntityReference[];
    spawnedFromNodeId?: string;
    children?: QuestPlanItem[];
};

export type QuestPlan = QuestPlanItem[];

export type CreateQuestRequest = {
    name: string;
    objective?: string;
    contextMarkdown?: string;
    projectMarkdown?: string;
    plan?: QuestPlan;
};

export type QuestNotebookEdit =
    | { append: string }
    | { oldText: string; newText: string };

export type UpdateQuestReportRequest = {
    /** Legacy full replacement. Agent tools use notebookEdit for incremental changes. */
    projectMarkdown?: string;
    notebookEdit?: QuestNotebookEdit;
    objective?: string;
    contextMarkdown?: string;
    objectiveApprovalHumanInputId?: string;
};

export type UpdateQuestVisibilityRequest = {
    visibility: QuestVisibility;
};

export type UpdateQuestNotificationSettingsRequest = {
    notificationSettings: SrtNotificationConfig;
};

export type AppendQuestActivityLogRequest = {
    eventType: QuestActivityEventType;
    summary: string;
    detailsMarkdown?: string;
    metadata?: Record<string, unknown>;
};

export type SteerQuestRequest = {
    sourceMessageId?: string;
    message: string;
    sourceSessionId?: string;
    source?: QuestSteerSource;
};

export type StartQuestChatRequest = {
    title: string;
    openingMessage: string;
};

export type ContinueQuestChatRequest = {
    message: string;
};

export type QuestHealth = {
    status: QuestHealthStatus;
    driverStatus: QuestDriverStatus;
    hasActiveWait: boolean;
    hasFutureScheduledTask: boolean;
    hasRecentQuestActivity: boolean;
    hasRecentTaskActivity: boolean;
    hasDriverChat: boolean;
    isDriverRunning: boolean;
};

export type Quest = {
    displayStatus?: QuestDisplayStatus;
    displayDetails?: QuestDisplayDetails;
    id: string;
    name: string;
    objective: string;
    status: QuestStatus;
    sessionId: string | null;
    visibility: QuestVisibility;
    projectMarkdown: string;
    plan: QuestPlan;
    notificationSettings: SrtNotificationConfig;
    projectUuid: string;
    organizationUuid: string;
    createdBy: string;
    updatedBy: string | null;
    createdAt: Date;
    updatedAt: Date;
};

export type QuestAccess = {
    isOwner: boolean;
    canManage: boolean;
    canViewHumanInputs: boolean;
    canPauseResume: boolean;
    canChangeVisibility: boolean;
    canConfigureNotifications: boolean;
    canDelete: boolean;
    canStartPersonalChat: boolean;
};

export type QuestWithAccess = Quest & {
    access: QuestAccess;
};

export type SerializedQuest = Omit<Quest, 'createdAt' | 'updatedAt'> & {
    createdAt: string;
    updatedAt: string;
};

export type SerializedQuestWithAccess = Omit<
    QuestWithAccess,
    'createdAt' | 'updatedAt'
> & {
    createdAt: string;
    updatedAt: string;
};

export type QuestListItem = Quest & {
    pendingHumanInputsCount: number;
};

export type QuestListItemWithAccess = QuestWithAccess & {
    pendingHumanInputsCount: number;
};

export type SerializedQuestListItem = Omit<
    QuestListItemWithAccess,
    'createdAt' | 'updatedAt'
> & {
    createdAt: string;
    updatedAt: string;
};

export type CreateQuest = Pick<
    Quest,
    | 'name'
    | 'objective'
    | 'status'
    | 'sessionId'
    | 'projectMarkdown'
    | 'projectUuid'
    | 'organizationUuid'
    | 'createdBy'
> &
    Partial<Pick<Quest, 'plan' | 'visibility'>>;

export type UpdateQuest = Partial<
    Pick<
        Quest,
        | 'name'
        | 'objective'
        | 'status'
        | 'sessionId'
        | 'visibility'
        | 'projectMarkdown'
        | 'plan'
        | 'notificationSettings'
        | 'updatedBy'
    >
>;

export type QuestHandoff = {
    id: string;
    questId: string;
    agentTaskId: string;
    taskSessionId: string | null;
    insightId: string;
    status: QuestHandoffStatus;
    promptStatus: QuestHandoffPromptStatus | null;
    summary: string;
    handoffMarkdown?: string | null;
    payloadRevision?: number;
    structuredData: QuestHandoffStructuredData | null;
    createdAt: Date;
};

export type CreateQuestHandoff = Pick<
    QuestHandoff,
    | 'questId'
    | 'agentTaskId'
    | 'taskSessionId'
    | 'insightId'
    | 'status'
    | 'summary'
> &
    Partial<Pick<QuestHandoff, 'handoffMarkdown' | 'structuredData'>>;

export type QuestHandoffQuery = {
    agentTaskId?: string;
    insightId?: string;
    planItemId?: string;
    beforeHandoffId?: string;
};

export type QuestHumanInput = {
    id: string;
    questId: string;
    status: QuestHumanInputStatus;
    title: string;
    markdownContent: string;
    options: QuestHumanInputOption[] | null;
    responseMarkdown: string | null;
    answeredAt: Date | null;
    resumedAt: Date | null;
    createdAt: Date;
    updatedAt: Date;
};

export type OpenQuestHumanInput = QuestHumanInput & {
    questName: string;
};

export type QuestHumanInputSummary = Pick<
    QuestHumanInput,
    | 'id'
    | 'questId'
    | 'status'
    | 'title'
    | 'answeredAt'
    | 'resumedAt'
    | 'createdAt'
    | 'updatedAt'
> & {
    /** Derived from the existing answer options; not a separate input type. */
    isMilestoneKickoff?: boolean;
};

export type QuestHumanInputOption = {
    id: string;
    label: string;
    description?: string;
    recommended?: boolean;
};

export const QUEST_OBJECTIVE_APPROVAL_HUMAN_INPUT_TITLE =
    'Approve objective change';
export const QUEST_OBJECTIVE_APPROVE_OPTION_ID = 'approve-objective-change';
export const QUEST_OBJECTIVE_KEEP_OPTION_ID = 'keep-current-objective';
export const QUEST_MILESTONE_CONTINUE_OPTION_ID = 'start-next-milestone';
export const QUEST_HUMAN_INPUT_SKIP_RESPONSE =
    "I don't know. Use the available context to make the best reasonable assumption, explain it, and continue. This is not approval to start another milestone or take a consequential action.";

export const getReadableQuestInputResponse = (response: string): string =>
    response.replace(/^Selected option: (.+) \(id: [^)\n]+\)(?=\n|$)/, '$1');

export const buildQuestHumanInputOptionResponse = (
    option: Pick<QuestHumanInputOption, 'id' | 'label' | 'description'>,
): string => {
    const responseLines = [option.label];

    if (option.description) {
        responseLines.push(option.description);
    }

    return responseLines.join('\n');
};

export const getQuestObjectiveApprovalOptions = ({
    currentObjective,
    proposedObjective,
}: {
    currentObjective: string;
    proposedObjective: string;
}): QuestHumanInputOption[] => [
    {
        id: QUEST_OBJECTIVE_APPROVE_OPTION_ID,
        label: 'Approve objective change',
        description: `Change the original objective to:\n${proposedObjective}`,
        recommended: true,
    },
    {
        id: QUEST_OBJECTIVE_KEEP_OPTION_ID,
        label: 'Keep current objective',
        description: `Keep the current original objective:\n${currentObjective}`,
    },
];

export type SerializedQuestHumanInput = Omit<
    QuestHumanInput,
    'answeredAt' | 'createdAt' | 'resumedAt' | 'updatedAt'
> & {
    answeredAt: string | null;
    resumedAt: string | null;
    createdAt: string;
    updatedAt: string;
};

export type SerializedOpenQuestHumanInput = Omit<
    OpenQuestHumanInput,
    'answeredAt' | 'createdAt' | 'resumedAt' | 'updatedAt'
> & {
    answeredAt: string | null;
    resumedAt: string | null;
    createdAt: string;
    updatedAt: string;
};

export type SerializedQuestHumanInputSummary = Omit<
    QuestHumanInputSummary,
    'answeredAt' | 'createdAt' | 'resumedAt' | 'updatedAt'
> & {
    answeredAt: string | null;
    resumedAt: string | null;
    createdAt: string;
    updatedAt: string;
};

export type CreateQuestHumanInput = Pick<
    QuestHumanInput,
    'questId' | 'status' | 'title' | 'markdownContent'
> &
    Partial<Pick<QuestHumanInput, 'options'>>;

export type UpdateQuestHumanInput = Partial<
    Pick<
        QuestHumanInput,
        'status' | 'responseMarkdown' | 'answeredAt' | 'resumedAt'
    >
>;

export type QuestWait = {
    reason?: QuestWaitReason;
    id: string;
    questId: string;
    status: QuestWaitStatus;
    watchedAgentTaskIds: string[];
    deadlineAt: Date;
    schedulerJobId: string | null;
    resumeMessage: string;
    resolvedByHandoffId: string | null;
    createdBy: string;
    createdAt: Date;
    updatedAt: Date;
    resolvedAt: Date | null;
};

export type CreateQuestWait = Pick<
    QuestWait,
    | 'reason'
    | 'questId'
    | 'watchedAgentTaskIds'
    | 'deadlineAt'
    | 'resumeMessage'
    | 'createdBy'
>;

export type QuestActivityLog = {
    id: string;
    questId: string;
    eventType: QuestActivityEventType;
    summary: string;
    detailsMarkdown: string | null;
    metadata: Record<string, unknown> | null;
    createdAt: Date;
};

export type SerializedQuestActivityLog = Omit<QuestActivityLog, 'createdAt'> & {
    createdAt: string;
};

export type CreateQuestActivityLog = Pick<
    QuestActivityLog,
    'questId' | 'eventType' | 'summary' | 'detailsMarkdown' | 'metadata'
>;

export type ApiQuestsResponse = {
    status: 'ok';
    results: KnexPaginatedData<QuestListItemWithAccess[]>;
};
